import { useEffect, useMemo, useState } from 'react'
import type { ServerApiClient } from '../../../data/ServerApiClient'
import { openCfoBillingPortal, startCfoCheckout } from './cfoBilling'
import { trimStr } from './cfoFormat'
import { invalidateCfoBootstrapCache, useCfoBootstrap } from './useCfoBootstrap'

/**
 * Trial / billing state banner shown at the top of every CFO interior page.
 *
 * Renders nothing for legacy tenants (no plan recorded), while a subscription
 * is healthy, or while the plan payload is missing (older server, bootstrap
 * still loading). The three states it does surface:
 *  - trial running, no subscription → days left + choose-a-plan buttons
 *  - trial over, no live subscription → Essentials notice + choose-a-plan
 *  - subscription past_due → update-payment warning → Billing Portal
 */
export function CfoPlanBanner({
  appApi,
  workbookId,
}: {
  appApi: ServerApiClient
  workbookId: string | null
}) {
  // Tolerate partial appApi mocks (tests render CfoInteriorPage with `{}`).
  const bootstrapCapable = typeof (appApi as any)?.getQuickBooksCfoCoreBootstrap === 'function'
  const { bootstrap } = useCfoBootstrap(appApi, bootstrapCapable ? workbookId : null)
  const [busy, setBusy] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [justReturned, setJustReturned] = useState<'success' | 'canceled' | null>(null)
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly')
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  // Returning from Stripe: refresh the cached plan info and clean the URL.
  useEffect(() => {
    let marker: string | null = null
    try {
      const url = new URL(window.location.href)
      marker = url.searchParams.get('billing')
      if (marker) {
        url.searchParams.delete('billing')
        window.history.replaceState(null, '', url.toString())
      }
    } catch {
      /* ignore */
    }
    if (marker === 'success') {
      invalidateCfoBootstrapCache(workbookId)
      setJustReturned('success')
    } else if (marker === 'canceled') {
      setJustReturned('canceled')
    }
  }, [workbookId])

  const plan = bootstrap?.plan
  const trialDaysLeft = useMemo(() => {
    const ends = trimStr(plan?.trial_ends_at)
    if (!ends) return null
    const ms = new Date(ends).getTime() - nowMs
    if (!Number.isFinite(ms)) return null
    return Math.max(0, Math.ceil(ms / 86_400_000))
  }, [nowMs, plan?.trial_ends_at])

  if (justReturned === 'success') {
    return (
      <article className="cfo-shell-notice" role="status" style={{ marginBottom: 'var(--s3)' }}>
        <strong>You’re all set</strong>
        <p style={{ margin: '6px 0 0', color: 'var(--muted)' }}>
          Your subscription is active. A receipt is on its way to your inbox.
        </p>
      </article>
    )
  }

  // No plan payload (legacy tenant / older server / still loading) → nothing.
  if (!plan || !trimStr(plan.plan_code)) return null

  const entitled = ['active', 'trialing', 'past_due'].includes(trimStr(plan.plan_status))
  const run = (key: string, fn: () => Promise<void>) => {
    setBusy(key)
    setError('')
    fn().catch((err: any) => {
      setBusy('')
      setError(trimStr(err?.message || err) || 'Something went wrong — please try again.')
    })
  }
  const wb = trimStr(workbookId)

  if (plan.plan_status === 'past_due' && wb) {
    return (
      <article className="cfo-shell-notice" role="alert" style={{ marginBottom: 'var(--s3)' }}>
        <strong>There’s a payment issue on your subscription</strong>
        <p style={{ margin: '6px 0', color: 'var(--muted)' }}>
          Your last payment didn’t go through. Update your card and we’ll retry — nothing is
          interrupted yet.
        </p>
        <button
          type="button"
          className="btn ghost"
          disabled={busy !== ''}
          onClick={() => run('portal', () => openCfoBillingPortal(appApi, wb))}
        >
          {busy === 'portal' ? 'Opening…' : 'Update payment method'}
        </button>
        {error ? <p style={{ margin: '6px 0 0', color: 'var(--muted)' }}>{error}</p> : null}
      </article>
    )
  }


  if (trimStr(plan.plan_code) === 'partner' && !entitled && wb) {
    const trialRunning = Boolean(plan.trial_active) && (trialDaysLeft == null || trialDaysLeft > 0)
    const headline = trialRunning
      ? trialDaysLeft != null
        ? `Your Partner trial has ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left`
        : 'Your Partner trial is running'
      : 'Your Partner trial has ended'
    return (
      <article className="cfo-shell-notice" role="status" style={{ marginBottom: 'var(--s3)' }}>
        <strong>{headline}</strong>
        <p style={{ margin: '6px 0', color: 'var(--muted)' }}>
          Partner billing is monthly and automatically follows your provisioned client count.
        </p>
        <button
          type="button"
          className="btn"
          onClick={() => window.location.assign(`/billing/partner?workbook_id=${encodeURIComponent(wb)}`)}
        >
          Review Partner billing
        </button>
      </article>
    )
  }

  if (entitled || !wb) return null

  const trialRunning = Boolean(plan.trial_active) && (trialDaysLeft == null || trialDaysLeft > 0)
  const headline = trialRunning
    ? trialDaysLeft != null
      ? `Your Pro trial has ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left`
      : 'Your Pro trial is running'
    : 'Your Pro trial has ended'
  const body = trialRunning
    ? 'Everything you see is full Pro. Pick a plan whenever you’re ready — your remaining trial days carry over, no card needed until then.'
    : 'You’re on Essentials features now. Choose a plan to bring back the forecast-powered surfaces — takes about a minute.'

  return (
    <article className="cfo-shell-notice" role="status" style={{ marginBottom: 'var(--s3)' }}>
      <strong>{headline}</strong>
      <p style={{ margin: '6px 0', color: 'var(--muted)' }}>{body}</p>
      <div role="group" aria-label="Billing interval" style={{ display: 'inline-flex', gap: 4, marginBottom: 10 }}>
        {(['monthly', 'annual'] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            className={`btn ${billingInterval === opt ? '' : 'ghost'}`}
            aria-pressed={billingInterval === opt}
            disabled={busy !== ''}
            onClick={() => setBillingInterval(opt)}
            style={{ padding: '4px 12px', fontSize: 13 }}
          >
            {opt === 'monthly' ? 'Monthly' : 'Annual · save ~20%'}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn"
          disabled={busy !== ''}
          onClick={() => run('pro', () => startCfoCheckout(appApi, wb, 'pro', billingInterval))}
        >
          {busy === 'pro'
            ? 'Starting checkout…'
            : billingInterval === 'monthly'
              ? 'Continue with Pro — $349/mo'
              : 'Continue with Pro — $279/mo, billed annually'}
        </button>
        <button
          type="button"
          className="btn ghost"
          disabled={busy !== ''}
          onClick={() => run('essentials', () => startCfoCheckout(appApi, wb, 'essentials', billingInterval))}
        >
          {busy === 'essentials'
            ? 'Starting checkout…'
            : billingInterval === 'monthly'
              ? 'Essentials — $99/mo'
              : 'Essentials — $79/mo, billed annually'}
        </button>
        {plan.has_subscription ? (
          <button
            type="button"
            className="btn ghost"
            disabled={busy !== ''}
            onClick={() => run('portal', () => openCfoBillingPortal(appApi, wb))}
          >
            {busy === 'portal' ? 'Opening…' : 'Manage billing'}
          </button>
        ) : null}
      </div>
      <p style={{ margin: '8px 0 0', color: 'var(--muted)', fontSize: 13 }}>
        {billingInterval === 'annual'
          ? 'Billed once a year. You can switch back to monthly anytime in Manage billing.'
          : 'Prefer to pay yearly and save ~20%? Choose Annual above.'}
      </p>
      {error ? <p style={{ margin: '6px 0 0', color: 'var(--muted)' }}>{error}</p> : null}
    </article>
  )
}
